// A status or severity in words with a dot (ENTRAID-347 redesign, brand/onguard/README.md "Lists inside cards"): the
// color lives only in the dot, the word is ink text, so color is never the only signal and amber is never a text
// color. Used by every list that shows a status: Activity events, flagged cases, collections.

export const TONE_DOT = Object.freeze({
  good: 'var(--good)',
  warn: 'var(--warn-dot)',
  bad: 'var(--bad)',
  info: 'var(--blue)',
  off: 'var(--track)',
});

const SEVERITY_TONE = Object.freeze({ critical: 'bad', high: 'bad', medium: 'warn', low: 'off', info: 'off' });
const SEVERITY_WORD = Object.freeze({ critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low', info: 'Info' });

export function Dot({ tone = 'off', className = '' }) {
  return <span aria-hidden="true" className={`inline-block h-2 w-2 shrink-0 rounded-sm ${className}`.trim()} style={{ background: TONE_DOT[tone] ?? TONE_DOT.off }} />;
}

/** A dot in `tone` and the word. `strong` (default) is a short status word on one line; false is a sentence that may wrap. */
export default function StatusWord({ tone, children, strong = true, testId, ...rest }) {
  return (
    <span className={`inline-flex items-center gap-2 text-ipc-ink ${strong ? 'whitespace-nowrap font-semibold' : ''}`} data-testid={testId} {...rest}>
      <Dot tone={tone} />
      {children}
    </span>
  );
}

/** A Watch or check severity (critical, high, medium, low, info) as a dot and a word. */
export function SeverityWord({ severity }) {
  if (!severity) return null;
  return <StatusWord tone={SEVERITY_TONE[severity] ?? 'off'} data-severity={severity}>{SEVERITY_WORD[severity] ?? severity}</StatusWord>;
}

/**
 * B4: "Expected" with a simple check mark. The icon is decorative (the word is the accessible text), so a screen
 * reader reads "Expected" and color is never the only signal. `suffix` adds text such as " (admin)".
 */
export function ExpectedWord({ children = 'Expected', suffix = '', className = '' }) {
  return (
    <span className={`inline-flex items-center gap-1 text-ipc-ink ${className}`.trim()} data-testid="expected-word">
      <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" className="h-4 w-4 shrink-0" fill="none" stroke="var(--good)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
      {children}{suffix}
    </span>
  );
}
